import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Building2,
  Briefcase,
  Server,
  Calendar,
  Users,
  Search,
  LogOut,
  Menu,
  X,
  Shield,
  DollarSign,
  Receipt,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  Table,
  RefreshCw,
  Megaphone,
  Globe,
  FileText,
  ClipboardList,
  UserCircle,
  HelpCircle,
} from 'lucide-react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { auditAPI, checkApiReachable } from '../services/api';
import { useAppUpdateCheck } from '../hooks/useAppUpdateCheck';
import AppUpdateBanner from './AppUpdateBanner';
import { isSentryFeedbackAvailable, openSentryFeedback } from '../sentry.js';
import { getSyncDashboardUrl } from '../utils/syncDashboardUrl.js';
import { suppressPostLoginReturnTo } from '../utils/navigationReturn.js';

const Layout = () => {
  const { logout, isSuperAdmin, can, canAny, canBrowseFtoEntities, isSidebarMenuVisible } = useAuth();
  const navigate = useNavigate();
  const showAdminNav = canAny('users.view', 'system.configure') || isSuperAdmin;
  /** Sync ops and Visitors remain SuperAdmin-capability; visibility also honors hide overrides. */
  const showSyncDashboard = isSuperAdmin && isSidebarMenuVisible('sync-dashboard');
  const showVisitors = isSuperAdmin && isSidebarMenuVisible('visitors');
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [infraTaxMenuOpen, setInfraTaxMenuOpen] = useState(false);
  const [tasksMenuOpen, setTasksMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [apiUnreachable, setApiUnreachable] = useState(false);
  const [apiCheckDismissed, setApiCheckDismissed] = useState(false);
  const { updateAvailable, dismiss: dismissAppUpdate, refresh: refreshApp } = useAppUpdateCheck();
  const lastTrackedPathRef = useRef('');

  const runApiReachabilityCheck = useCallback(async () => {
    const result = await checkApiReachable(5000);
    setApiUnreachable(!result.reachable);
    if (result.reachable) setApiCheckDismissed(false);
    return result.reachable;
  }, []);

  // Delay health check so cold proxy can warm up on first login (reload fixes it otherwise)
  useEffect(() => {
    if (apiCheckDismissed) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      const result = await checkApiReachable(5000);
      if (cancelled) return;
      setApiUnreachable(!result.reachable);
    }, 5000);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [apiCheckDismissed]);

  const infrastructureTaxSubmenu = [
    { name: 'Property-Level Table', href: '/infrastructure-tax/properties', icon: Table },
    { name: 'Alerts', href: '/infrastructure-tax/compliance', icon: AlertTriangle },
  ];

  const tasksSubmenu = [
    { name: 'Templates', href: '/tasks/templates', icon: FileText, permission: 'tasks.manage_templates' },
  ].filter((item) => !item.permission || can(item.permission));

  const isSubItemActive = (subItem, pathname) => {
    if (subItem.href === '/tasks') return pathname === '/tasks';
    return pathname === subItem.href || pathname.startsWith(`${subItem.href}/`);
  };

  const isNavItemActive = (item, pathname) => {
    if (pathname === item.href) return true;
    if (item.href === '/pms-suppliers' && pathname.startsWith('/pms-suppliers/')) return true;
    if (item.href === '/tasks') {
      return pathname.startsWith('/tasks') || pathname.startsWith('/compliance');
    }
    if (item.href === '/taxpayers' && pathname.startsWith('/taxpayers')) return true;
    if (item.href === '/fto-entities' && pathname.startsWith('/fto-entities')) return true;
    if (item.href === '/help' && pathname.startsWith('/help')) return true;
    if (item.submenu?.length) {
      return item.submenu.some((sub) => isSubItemActive(sub, pathname) || pathname === sub.href);
    }
    return false;
  };

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      permission: 'dashboard.view',
      menuKey: 'dashboard',
    },
    {
      name: 'VAT',
      href: '/vat',
      icon: DollarSign,
      permission: 'dashboard.view_tax_totals',
      menuKey: 'vat',
    },
    {
      name: 'Infrastructure Tax',
      href: '/infrastructure-tax',
      icon: Receipt,
      permission: 'dashboard.view_tax_totals',
      submenu: infrastructureTaxSubmenu,
      menuKey: 'infrastructure-tax',
    },
    {
      name: 'Tasks',
      href: '/tasks',
      icon: ClipboardList,
      permission: 'tasks.view',
      menuKey: 'tasks',
      ...(tasksSubmenu.length ? { submenu: tasksSubmenu } : {}),
    },
    {
      name: 'Properties',
      href: '/properties',
      icon: Building2,
      permission: 'properties.view',
      menuKey: 'properties',
    },
    {
      name: 'Taxpayers',
      href: '/taxpayers',
      icon: Briefcase,
      permission: 'taxpayers.view',
      menuKey: 'taxpayers',
    },
    ...(canBrowseFtoEntities
      ? [
          {
            name: 'FTO entities',
            href: '/fto-entities',
            icon: Globe,
            permission: 'taxpayers.view',
            menuKey: 'fto-entities',
          },
        ]
      : []),
    // Full PMS supplier directory only when they have the supplier module permission.
    ...(can('pms_suppliers.view') || isSuperAdmin
      ? [
          {
            name: 'PMS Suppliers',
            href: '/pms-suppliers',
            icon: Server,
            permission: 'pms_suppliers.view',
            menuKey: 'pms-suppliers',
          },
        ]
      : []),
    {
      name: 'Bookings',
      href: '/bookings',
      icon: Calendar,
      permission: 'bookings.view',
      menuKey: 'bookings',
    },
    ...(showVisitors
      ? [{ name: 'Visitors', href: '/visitors', icon: Users, menuKey: 'visitors' }]
      : []),
    {
      name: 'Search',
      href: '/search',
      icon: Search,
      permissions: ['properties.view', 'taxpayers.view', 'bookings.view'],
      menuKey: 'search',
    },
    { name: 'Help', href: '/help', icon: HelpCircle, permission: 'help.view', menuKey: 'help' },
    ...(showSyncDashboard
      ? [
          {
            name: 'Sync Dashboard',
            href: getSyncDashboardUrl(),
            icon: RefreshCw,
            external: true,
            menuKey: 'sync-dashboard',
          },
        ]
      : []),
    ...(showAdminNav
      ? [{ name: 'Admin', href: '/admin', icon: Shield, menuKey: 'admin' }]
      : []),
  ].filter((item) => {
    if (item.menuKey && !isSidebarMenuVisible(item.menuKey)) return false;
    if (item.permission && !can(item.permission)) return false;
    if (item.permissions?.length && !canAny(...item.permissions)) return false;
    return true;
  });

  const isInfraTaxSubmenuActive = location.pathname.startsWith('/infrastructure-tax/');

  const isTasksSubmenuActive =
    location.pathname.startsWith('/tasks/templates') ||
    location.pathname.startsWith('/compliance/templates');

  // Auto-expand menus when a submenu route is active
  useEffect(() => {
    if (isInfraTaxSubmenuActive) {
      setInfraTaxMenuOpen(true);
    }
  }, [location.pathname, isInfraTaxSubmenuActive]);

  useEffect(() => {
    if (isTasksSubmenuActive) {
      setTasksMenuOpen(true);
    }
  }, [location.pathname, isTasksSubmenuActive]);

  useEffect(() => {
    const currentPath = `${location.pathname}${location.search || ''}`;
    if (currentPath === lastTrackedPathRef.current) return;
    lastTrackedPathRef.current = currentPath;
    auditAPI
      .trackPageVisit({ path: currentPath, title: document?.title || '' })
      .catch(() => {});
  }, [location.pathname, location.search]);

  const handleReportIssue = useCallback(async (event) => {
    setMobileMenuOpen(false);
    await openSentryFeedback(event?.currentTarget);
  }, []);

  const showFeedbackAction = isSentryFeedbackAvailable();

  const handleLogout = async () => {
    suppressPostLoginReturnTo();
    await logout();
    navigate('/login', { replace: true });
  };

  // Give wide data tables (properties, bookings, infra tax property table) more room on large displays.
  const isPropertiesRoute = location.pathname.startsWith('/properties');
  const isBookingsRoute = location.pathname.startsWith('/bookings');
  const isInfrastructureTaxPropertiesRoute = location.pathname.startsWith('/infrastructure-tax/properties');
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isWideTableRoute = isPropertiesRoute || isBookingsRoute || isInfrastructureTaxPropertiesRoute;
  const contentContainerClass = isWideTableRoute
    ? 'max-w-[1800px] mx-auto px-4 sm:px-6 md:px-8 w-full min-w-0'
    : isAdminRoute
      ? 'max-w-[1600px] mx-auto px-4 sm:px-6 md:px-8'
      : 'max-w-7xl mx-auto px-4 sm:px-6 md:px-8';

  const showAppUpdateBanner = updateAvailable;

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <AppUpdateBanner visible={showAppUpdateBanner} onRefresh={refreshApp} onDismiss={dismissAppUpdate} />
      {/* Sidebar */}
      <div className={`hidden md:flex md:flex-col md:fixed md:inset-y-0 transition-all duration-300 ${sidebarCollapsed ? 'md:w-16' : 'md:w-56'}`}>
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto">
          <div className="flex items-center justify-between flex-shrink-0 px-4 mb-4">
            {!sidebarCollapsed && <h1 className="text-xl font-bold text-primary-600">Zan Gateway</h1>}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-5 w-5" />
              ) : (
                <ChevronLeft className="h-5 w-5" />
              )}
            </button>
          </div>
          <div className="mt-4 flex-grow flex flex-col">
            <nav className="flex-1 px-2 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = isNavItemActive(item, location.pathname);
                const isExpanded =
                  item.submenu &&
                  !sidebarCollapsed &&
                  ((item.name === 'Infrastructure Tax' && infraTaxMenuOpen) ||
                    (item.name === 'Tasks' && tasksMenuOpen));
                
                return (
                  <div key={item.name}>
                    <div className="flex items-center">
                      {item.external ? (
                        <a
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-700 hover:bg-gray-50 hover:text-gray-900 group flex-1 flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors"
                          title={sidebarCollapsed ? item.name : ''}
                        >
                          <Icon
                            className={`text-gray-400 group-hover:text-gray-500 ${sidebarCollapsed ? 'mr-0' : 'mr-3'} h-5 w-5 flex-shrink-0`}
                          />
                          {!sidebarCollapsed && <span>{item.name}</span>}
                        </a>
                      ) : (
                        <Link
                          to={item.href}
                          className={`${
                            isActive && !item.submenu
                              ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                              : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          } group flex-1 flex items-center ${sidebarCollapsed ? 'px-3 justify-center' : 'px-3'} py-2 text-sm font-medium rounded-md transition-colors`}
                          title={sidebarCollapsed ? item.name : ''}
                        >
                          <Icon
                            className={`${
                              isActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                            } ${sidebarCollapsed ? 'mr-0' : 'mr-3'} h-5 w-5 flex-shrink-0`}
                          />
                          {!sidebarCollapsed && <span>{item.name}</span>}
                        </Link>
                      )}
                      {item.submenu && !sidebarCollapsed && (
                        <button
                          onClick={() => {
                            if (item.name === 'Infrastructure Tax') setInfraTaxMenuOpen(!infraTaxMenuOpen);
                            if (item.name === 'Tasks') setTasksMenuOpen(!tasksMenuOpen);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                    {item.submenu && isExpanded && !sidebarCollapsed && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = isSubItemActive(subItem, location.pathname);
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className={`${
                                isSubActive
                                  ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              } group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors`}
                            >
                              <SubIcon
                                className={`${
                                  isSubActive ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-500'
                                } mr-3 h-4 w-4`}
                              />
                              {subItem.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
          <div className="flex-shrink-0 flex flex-col gap-1 border-t border-gray-200 p-4">
            {showFeedbackAction ? (
              <button
                type="button"
                onClick={handleReportIssue}
                className="flex-shrink-0 w-full group block"
                title={sidebarCollapsed ? 'Report issue' : ''}
              >
                <div
                  className={`flex items-center text-gray-700 hover:text-gray-900 ${sidebarCollapsed ? 'justify-center' : ''}`}
                >
                  <Megaphone
                    className={`h-5 w-5 text-gray-400 group-hover:text-gray-500 ${sidebarCollapsed ? 'mr-0' : 'mr-3'}`}
                  />
                  {!sidebarCollapsed && <span className="text-sm font-medium">Report issue</span>}
                </div>
              </button>
            ) : null}
            <Link
              to="/profile"
              className="flex-shrink-0 w-full group block"
              title={sidebarCollapsed ? 'Profile' : ''}
            >
              <div
                className={`flex items-center text-gray-700 hover:text-gray-900 ${sidebarCollapsed ? 'justify-center' : ''}`}
              >
                <UserCircle
                  className={`h-5 w-5 text-gray-400 group-hover:text-gray-500 ${sidebarCollapsed ? 'mr-0' : 'mr-3'}`}
                />
                {!sidebarCollapsed && <span className="text-sm font-medium">Profile</span>}
              </div>
            </Link>
            <button
              onClick={handleLogout}
              className="flex-shrink-0 w-full group block"
              title={sidebarCollapsed ? 'Logout' : ''}
            >
              <div className={`flex items-center text-gray-700 hover:text-gray-900 ${sidebarCollapsed ? 'justify-center' : ''}`}>
                <LogOut className={`h-5 w-5 text-gray-400 group-hover:text-gray-500 ${sidebarCollapsed ? 'mr-0' : 'mr-3'}`} />
                {!sidebarCollapsed && <span className="text-sm font-medium">Logout</span>}
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu button */}
      <div className={`md:hidden fixed left-0 right-0 z-40 bg-white border-b border-gray-200 overflow-x-hidden ${showAppUpdateBanner ? 'top-10' : 'top-0'}`}>
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-xl font-bold text-primary-600 min-w-0 truncate">Zan Gateway</h1>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-gray-600 hover:text-gray-900 flex-shrink-0 ml-2"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="px-2 pt-2 pb-3 space-y-1 border-t border-gray-200">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = isNavItemActive(item, location.pathname);
              const isExpanded =
                item.submenu &&
                ((item.name === 'Infrastructure Tax' && infraTaxMenuOpen) ||
                  (item.name === 'Tasks' && tasksMenuOpen));
              
              return (
                <div key={item.name}>
                  <div className="flex items-center">
                    {item.external ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setMobileMenuOpen(false)}
                        className="text-gray-700 hover:bg-gray-50 group flex-1 flex items-center px-3 py-2 text-base font-medium rounded-md"
                      >
                        <Icon className="text-gray-400 mr-3 h-5 w-5" />
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        onClick={() => {
                          if (!item.submenu) setMobileMenuOpen(false);
                        }}
                        className={`${
                          isActive && !item.submenu
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        } group flex-1 flex items-center px-3 py-2 text-base font-medium rounded-md`}
                      >
                        <Icon
                          className={`${
                            isActive ? 'text-primary-600' : 'text-gray-400'
                          } mr-3 h-5 w-5`}
                        />
                        {item.name}
                      </Link>
                    )}
                    {item.submenu && (
                      <button
                        onClick={() => {
                          if (item.name === 'Infrastructure Tax') setInfraTaxMenuOpen(!infraTaxMenuOpen);
                          if (item.name === 'Tasks') setTasksMenuOpen(!tasksMenuOpen);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                  {item.submenu && isExpanded && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.submenu.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = isSubItemActive(subItem, location.pathname);
                        return (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`${
                              isSubActive
                                ? 'bg-primary-50 text-primary-700'
                                : 'text-gray-600 hover:bg-gray-50'
                            } group flex items-center px-3 py-2 text-sm font-medium rounded-md`}
                          >
                            <SubIcon
                              className={`${
                                isSubActive ? 'text-primary-600' : 'text-gray-400'
                              } mr-3 h-4 w-4`}
                            />
                            {subItem.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {showFeedbackAction ? (
              <button
                type="button"
                onClick={handleReportIssue}
                className="w-full group flex items-center px-3 py-2 text-base font-medium rounded-md text-gray-700 hover:bg-gray-50"
              >
                <Megaphone className="mr-3 h-5 w-5 text-gray-400" />
                Report issue
              </button>
            ) : null}
            <Link
              to="/profile"
              onClick={() => setMobileMenuOpen(false)}
              className="w-full group flex items-center px-3 py-2 text-base font-medium rounded-md text-gray-700 hover:bg-gray-50"
            >
              <UserCircle className="mr-3 h-5 w-5 text-gray-400" />
              Profile
            </Link>
            <button
              onClick={handleLogout}
              className="w-full group flex items-center px-3 py-2 text-base font-medium rounded-md text-gray-700 hover:bg-gray-50"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              Logout
            </button>
          </div>
        )}
      </div>

      {/* Main content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'md:pl-16' : 'md:pl-56'} ${showAppUpdateBanner ? 'md:pt-10' : 'md:pt-0'} ${showAppUpdateBanner ? 'pt-[6.5rem]' : 'pt-16'}`}>
        <main className="py-6">
          {apiUnreachable && !apiCheckDismissed && (
            <div className={`${contentContainerClass} mb-4`}>
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 flex flex-wrap items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-amber-800">
                    Could not reach the API.
                  </p>
                  <p className="text-sm text-amber-700 mt-0.5">
                    Try refreshing the page, or retry below.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    type="button"
                    onClick={async () => { await runApiReachabilityCheck(); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-amber-200 text-amber-900 hover:bg-amber-300"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Retry
                  </button>
                  <button
                    type="button"
                    onClick={() => setApiCheckDismissed(true)}
                    className="inline-flex items-center p-1.5 rounded-md text-amber-600 hover:bg-amber-200"
                    title="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className={contentContainerClass}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;

